import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useRecoilValueLoadable } from 'recoil';
import { BlockUI } from 'primereact/blockui';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton } from 'primereact/radiobutton';
import {
  EARLY_SCRAP_ATTACHMENT_BLUEPRINT,
  EARLY_SCRAP_FORM_META,
  EARLY_SCRAP_INSPECTION_BLUEPRINT,
  EARLY_SCRAP_JUDGMENT_BLUEPRINT,
  EARLY_SCRAP_PHOTO_BLUEPRINT,
  createEarlyScrapDefaultForm,
  formatEarlyScrapInspectNumber,
  mapMolitVehicleToEarlyScrap,
  isEarlyScrapConstructionMachinery,
} from '../../constants/EarlyScrapConstants';
import { PAYMENT_METHOD, YN } from '../../constants/Constants';
import {
  EarlyScrapConfirmationMethod,
} from '../../enums/EarlyScrapEnums';
import PerformanceCheckKind from '../../enums/PerformanceCheckKind';
import { EarlyScrapService } from '../../services/EarlyScrapService';
import { ServiceProvider } from '../../services';
import { MyInfoUtil } from '../../utils/myInfoUtil';
import { myInfoSelector } from '../../recoil/selectors';
import _ from 'lodash';
import dayjs from 'dayjs';
import './EarlyScrapRegistrationForm.css';
import EarlyScrapPrintDocument from './EarlyScrapPrintDocument';

const earlyScrapService = EarlyScrapService.getInstance();
const priceService = ServiceProvider.price;
const molitService = ServiceProvider.molit;

const RadioField = ({ name, control, options, disabled = false }) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <div className="early-scrap-radio-group">
        {options.map((option) => (
          <div key={option.value} className="early-scrap-radio-item">
            <RadioButton
              inputId={`${name}-${option.value}`}
              name={field.name}
              value={option.value}
              onChange={(e) => field.onChange(e.value)}
              checked={field.value === option.value}
              disabled={disabled}
            />
            <label htmlFor={`${name}-${option.value}`}>{option.label}</label>
          </div>
        ))}
      </div>
    )}
  />
);

const TextField = ({ name, control, placeholder, className = 'w-full' }) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <InputText {...field} className={className} placeholder={placeholder} />
    )}
  />
);

const EARLY_SCRAP_PRINT_STYLE_ID = 'early-scrap-print-page-style';

const applyEarlyScrapPrintPageStyle = () => {
  document.body.classList.add('early-scrap-printing');
  if (!document.getElementById(EARLY_SCRAP_PRINT_STYLE_ID)) {
    const styleEl = document.createElement('style');
    styleEl.id = EARLY_SCRAP_PRINT_STYLE_ID;
    styleEl.textContent = '@page { size: A4 portrait; margin: 0; }';
    document.head.appendChild(styleEl);
  }
};

const clearEarlyScrapPrintPageStyle = () => {
  document.body.classList.remove('early-scrap-printing');
  document.getElementById(EARLY_SCRAP_PRINT_STYLE_ID)?.remove();
};

const EarlyScrapRegistrationForm = () => {
  const defaultValues = useMemo(() => createEarlyScrapDefaultForm(), []);
  const { control, handleSubmit, setValue, getValues } = useForm({
    defaultValues,
  });
  const [loading, setLoading] = useState(false);
  const [carInformationLoading, setCarInformationLoading] = useState(false);
  const [photoPreviews, setPhotoPreviews] = useState({});
  const [photoFiles, setPhotoFiles] = useState({});
  const [checkPriceTable, setCheckPriceTable] = useState(null);

  // 조기폐차 인쇄 중에만 portrait @page 적용 (전역 CSS는 성능지 landscape를 깨뜨림)
  useEffect(() => {
    window.addEventListener('beforeprint', applyEarlyScrapPrintPageStyle);
    window.addEventListener('afterprint', clearEarlyScrapPrintPageStyle);
    return () => {
      clearEarlyScrapPrintPageStyle();
      window.removeEventListener('beforeprint', applyEarlyScrapPrintPageStyle);
      window.removeEventListener('afterprint', clearEarlyScrapPrintPageStyle);
    };
  }, []);

  const myInfoLoadable = useRecoilValueLoadable(myInfoSelector);
  const myInfo = useMemo(
    () => MyInfoUtil.unpack(myInfoLoadable.contents),
    [myInfoLoadable.contents]
  );

  const vehicleClass = useWatch({ control, name: 'vehicle.vehicleClass' });
  const confirmationMethod = useWatch({
    control,
    name: 'confirmationMethod',
  });
  const checkPrice = useWatch({
    control,
    name: 'paymentInformation.checkPrice',
  });
  const checkPriceVAT = useWatch({
    control,
    name: 'paymentInformation.checkPriceVAT',
  });
  const discountedCheckPrice = useWatch({
    control,
    name: 'paymentInformation.discountedCheckPrice',
  });
  const isConstructionMachinery =
    isEarlyScrapConstructionMachinery(vehicleClass);
  const isOnlineConfirmation =
    confirmationMethod === EarlyScrapConfirmationMethod.ONLINE;
  const formValues = useWatch({ control });

  const applyCheckPrice = ({
    table = checkPriceTable,
    selectedItemId = getValues('paymentInformation.checkPriceItemId'),
    discount = getValues('paymentInformation.discountedCheckPrice'),
  } = {}) => {
    const { checkPriceTableId, checkPriceItems } = table || {};
    if (!checkPriceItems?.length) {
      setValue('paymentInformation.checkPriceTableId', checkPriceTableId || 0);
      setValue('paymentInformation.checkPriceItemId', '');
      setValue('paymentInformation.checkPrice', 0);
      setValue('paymentInformation.checkPriceVAT', 0);
      return;
    }

    const itemId = selectedItemId || _.get(checkPriceItems, '0.id');
    const item = _.find(checkPriceItems, { id: itemId }) || checkPriceItems[0];
    const total = _.toNumber(_.get(item, 'price')) || 0;
    const vat = Math.floor(total / 11 / 10) * 10;
    const price = total - vat;
    const discountAmount = _.toNumber(discount) || 0;

    setValue('paymentInformation.checkPriceTableId', checkPriceTableId || 0);
    setValue('paymentInformation.checkPriceItemId', item?.id ?? '');
    setValue('paymentInformation.checkPrice', price);
    setValue('paymentInformation.checkPriceVAT', vat);

    if (total - discountAmount <= 0) {
      setValue(
        'paymentInformation.checkPricePaidAt',
        dayjs().format('YYYY-MM-DDTHH:mm:ss')
      );
      setValue('paymentInformation.checkPricePaidYN', YN.YES);
    } else {
      setValue('paymentInformation.checkPricePaidAt', null);
      setValue('paymentInformation.checkPricePaidYN', YN.NO);
    }
  };

  useEffect(() => {
    if (!isConstructionMachinery) {
      setValue('vehicle.heavyEquipmentSpec', '');
      setValue('inspectionResults.hydraulic', null);
      setValue('inspectionResults.workingPartOperation', null);
      setValue('inspectionResults.workingPartNoise', null);
      return;
    }
    // 건설기계: 유압장치 3항목 기본 양호 (미선택 시 인쇄에 누락되지 않도록)
    if (!getValues('inspectionResults.hydraulic')) {
      setValue('inspectionResults.hydraulic', 'GOOD');
    }
    if (!getValues('inspectionResults.workingPartOperation')) {
      setValue('inspectionResults.workingPartOperation', 'GOOD');
    }
    if (!getValues('inspectionResults.workingPartNoise')) {
      setValue('inspectionResults.workingPartNoise', 'GOOD');
    }
  }, [isConstructionMachinery, setValue, getValues]);

  useEffect(() => {
    if (!isOnlineConfirmation) return;
    setPhotoPreviews({});
    setPhotoFiles({});
  }, [isOnlineConfirmation]);

  useEffect(() => {
    if (myInfoLoadable.state !== 'hasValue') return;

    const { myAssociationId, myEnterpriseId, myShopId, myShopName, myName } =
      myInfo;

    if (myShopName) {
      setValue('inspector.shopName', myShopName);
    }
    if (myName) {
      setValue('inspector.inspectorName', myName);
    }
    if (!getValues('inspector.checkDate')) {
      setValue('inspector.checkDate', dayjs().format('YYYY-MM-DD'));
    }

    (async () => {
      try {
        const { data } = await earlyScrapService.getNextInspectNumber({
          shopId: myShopId || 0,
        });
        const inspectNumber =
          data?.inspectNumber || formatEarlyScrapInspectNumber(0);
        setValue('inspectNumber', inspectNumber);
      } catch (error) {
        console.error('조기폐차 점검번호 채번 실패', error);
        setValue('inspectNumber', formatEarlyScrapInspectNumber(0));
        window.cerp?.toast?.warn?.(
          '점검번호 채번',
          '서버 채번에 실패해 임시 번호(당일 000000)를 표시합니다.'
        );
      }

      if (!myShopId && !myEnterpriseId) return;

      try {
        const { data } = await priceService.getCheckPrice({
          associationId: myAssociationId || 0,
          enterpriseId: myEnterpriseId || 0,
          shopId: myShopId || 0,
        });
        setCheckPriceTable(data || null);
        applyCheckPrice({
          table: data,
          selectedItemId: _.get(data, 'checkPriceItems.0.id'),
          discount: 0,
        });
      } catch (error) {
        console.error('조기폐차 점검료 조회 실패', error);
        setCheckPriceTable(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    myInfoLoadable.state,
    myInfo.myAssociationId,
    myInfo.myEnterpriseId,
    myInfo.myShopId,
  ]);

  const handlePhotoChange = (position, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setPhotoPreviews((prev) => ({ ...prev, [position]: previewUrl }));
    setPhotoFiles((prev) => ({ ...prev, [position]: file }));
  };

  const getCarInformation = async (event) => {
    event?.preventDefault?.();

    if (isConstructionMachinery) {
      window.cerp.dialog.warn(
        '차량조회',
        '건설기계는 국토부 조회 대상이 아닙니다. 명세를 수기로 입력해 주세요.'
      );
      return;
    }

    const licensePlateNumber = (
      getValues('vehicle.licensePlateNumber') || ''
    ).trim();

    if (!licensePlateNumber) {
      window.cerp.dialog.error(
        '차량조회 실패',
        '[EMPTY] 차량 번호를 입력하세요.'
      );
      return;
    }

    setCarInformationLoading(true);
    setLoading(true);
    try {
      const {
        data: {
          molitData: {
            RECORD1: [rawVehicleInformation],
          },
        },
      } = await molitService.vehicleInformation([licensePlateNumber]);

      if (!rawVehicleInformation) {
        throw { code: 'EMPTY', message: '조회된 차량정보가 없습니다.' };
      }

      const mapped = mapMolitVehicleToEarlyScrap(rawVehicleInformation);
      Object.entries(mapped).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          setValue(`vehicle.${key}`, value);
        }
      });
      setValue('vehicle.vehicleClass', 'AUTOMOBILE');

      window.cerp.toast.success(
        '차량정보조회',
        '국토부 TS 차량정보를 명세에 반영했습니다.'
      );
    } catch (error) {
      window.cerp.dialog.error(
        '차량조회 실패',
        `[${error?.code || 'ERROR'}] ${error?.message || '차량정보 조회에 실패했습니다.'}`
      );
    } finally {
      setCarInformationLoading(false);
      setLoading(false);
    }
  };

  const onSubmit = async (formData) => {
    setLoading(true);
    window.cerp.toast.showStickyToast('조기폐차 확인서를 전송하고 있습니다.');

    try {
      const files = EARLY_SCRAP_PHOTO_BLUEPRINT.positions
        .map(({ position }) => photoFiles[position])
        .filter(Boolean);

      await earlyScrapService.register(
        {
          checkKind: PerformanceCheckKind.EARLY_SCRAP,
          checkData: formData,
          photoPositions: Object.keys(photoFiles),
        },
        files
      );

      window.cerp.toast.success(
        '조기폐차 확인서 저장',
        '조기폐차 확인서가 전송되었습니다.'
      );
    } catch (error) {
      window.cerp.dialog.error(
        '조기폐차 확인서 저장 실패',
        `[${error?.code || 'ERROR'}] ${error?.message || '서버 연동 API가 준비되지 않았습니다.'}`
      );
    } finally {
      window.cerp.toast.clear();
      setLoading(false);
    }
  };

  return (
    <BlockUI blocked={loading} fullScreen>
      <div className="early-scrap-page">
        <div className="early-scrap-container early-scrap-screen-only">
          <div className="early-scrap-header">
            <div className="early-scrap-form-type">
              [{EARLY_SCRAP_FORM_META.formType}]
            </div>
            <h1>{EARLY_SCRAP_FORM_META.title}</h1>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="early-scrap-doc-meta">
              <label htmlFor="inspectNumber">점검번호 :</label>
              <Controller
                name="inspectNumber"
                control={control}
                render={({ field }) => (
                  <InputText
                    id="inspectNumber"
                    {...field}
                    readOnly
                    className="early-scrap-inspect-number"
                    placeholder="자동채번"
                    title="연월일(YYMMDD) + 6자리 순번(000000~999999)"
                  />
                )}
              />
            </div>

            <table className="early-scrap-table">
              <tbody>
                <tr>
                  <th rowSpan={3} className="category-th">
                    소<br />유<br />자
                  </th>
                  <th>성 명</th>
                  <td>
                    <TextField name="owner.name" control={control} />
                  </td>
                  <th>생년월일</th>
                  <td>
                    <TextField
                      name="owner.birthDate"
                      control={control}
                      placeholder="6자리"
                    />
                  </td>
                </tr>
                <tr>
                  <th>주 소</th>
                  <td colSpan={3}>
                    <TextField name="owner.address" control={control} />
                  </td>
                </tr>
                <tr>
                  <th>
                    휴대전화
                    <br />
                    (차주 본인)
                  </th>
                  <td>
                    <TextField
                      name="owner.phone"
                      control={control}
                      placeholder="010-0000-0000"
                    />
                  </td>
                  <th>FAX</th>
                  <td>
                    <TextField name="owner.fax" control={control} />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="early-scrap-table">
              <tbody>
                <tr>
                  <th rowSpan={3} className="category-th">
                    점<br />검<br />료
                  </th>
                  <th>점검료구분</th>
                  <td>
                    <Controller
                      name="paymentInformation.checkPriceItemId"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          {...field}
                          className="w-full"
                          options={checkPriceTable?.checkPriceItems || []}
                          optionLabel="title"
                          optionValue="id"
                          placeholder={
                            checkPriceTable?.checkPriceItems?.length
                              ? '선택'
                              : '점검료 없음'
                          }
                          disabled={!checkPriceTable?.checkPriceItems?.length}
                          onChange={(e) => {
                            field.onChange(e.value);
                            applyCheckPrice({ selectedItemId: e.value });
                          }}
                        />
                      )}
                    />
                  </td>
                  <th>점검료 (VAT포함)</th>
                  <td>
                    <InputNumber
                      className="w-full"
                      value={
                        (_.toNumber(checkPrice) || 0) +
                        (_.toNumber(checkPriceVAT) || 0)
                      }
                      suffix=" 원"
                      readOnly
                    />
                  </td>
                </tr>
                <tr>
                  <th>결제</th>
                  <td>
                    <Controller
                      name="paymentInformation.checkPricePaidBy"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          {...field}
                          className="w-full"
                          options={PAYMENT_METHOD}
                          onChange={(e) => field.onChange(e.value)}
                        />
                      )}
                    />
                  </td>
                  <th>할인 금액</th>
                  <td>
                    <Controller
                      name="paymentInformation.discountedCheckPrice"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          className="w-full"
                          value={field.value}
                          suffix=" 원"
                          onFocus={(e) => e.target.select()}
                          onValueChange={(e) => {
                            const value = e.value || 0;
                            field.onChange(value);
                            applyCheckPrice({ discount: value });
                          }}
                        />
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <th>할인사유</th>
                  <td colSpan={3}>
                    <Controller
                      name="paymentInformation.discountReason"
                      control={control}
                      render={({ field }) => (
                        <InputText
                          {...field}
                          className="w-full"
                          placeholder={
                            (_.toNumber(discountedCheckPrice) || 0) > 0
                              ? '할인 사유를 입력하세요.'
                              : ''
                          }
                        />
                      )}
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="early-scrap-table">
              <tbody>
                <tr>
                  <th rowSpan={5} className="category-th">
                    신<br />청<br />
                    <br />
                    자<br />동<br />차<br />
                    <br />
                    명<br />세
                  </th>
                  <th>차량번호</th>
                  <td>
                    <div className="early-scrap-plate-lookup">
                      <Controller
                        name="vehicle.licensePlateNumber"
                        control={control}
                        render={({ field }) => (
                          <InputText
                            {...field}
                            className="w-full"
                            placeholder={
                              isConstructionMachinery
                                ? '건설기계 번호 수기 입력'
                                : '차량번호 입력 후 조회'
                            }
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^ㄱ-힣0-9ㆍᆢㆍ‥ㆍ ᆢㆍ ᆢ]+/g,
                                ''
                              );
                              field.onChange(value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !isConstructionMachinery) {
                                getCarInformation(e);
                              }
                            }}
                          />
                        )}
                      />
                      <Button
                        type="button"
                        label="차량번호 조회"
                        icon="pi pi-search"
                        className="p-button-sm"
                        loading={carInformationLoading}
                        disabled={isConstructionMachinery}
                        title={
                          isConstructionMachinery
                            ? '건설기계는 수기 입력합니다.'
                            : '국토부 TS 차량정보 조회'
                        }
                        onClick={getCarInformation}
                      />
                    </div>
                  </td>
                  <th>차대번호</th>
                  <td>
                    <TextField
                      name="vehicle.vin"
                      control={control}
                      placeholder={
                        isConstructionMachinery ? '수기 입력' : ''
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <th>구 분</th>
                  <td colSpan={3}>
                    <Controller
                      name="vehicle.vehicleClass"
                      control={control}
                      render={({ field }) => (
                        <div className="early-scrap-radio-group">
                          <div className="early-scrap-radio-item">
                            <RadioButton
                              inputId="vehicle-automobile"
                              value="AUTOMOBILE"
                              onChange={(e) => field.onChange(e.value)}
                              checked={field.value === 'AUTOMOBILE'}
                            />
                            <label htmlFor="vehicle-automobile">
                              자동차 (국토부 조회)
                            </label>
                          </div>
                          <div className="early-scrap-radio-item">
                            <RadioButton
                              inputId="vehicle-construction"
                              value="CONSTRUCTION_MACHINERY"
                              onChange={(e) => field.onChange(e.value)}
                              checked={field.value === 'CONSTRUCTION_MACHINERY'}
                            />
                            <label htmlFor="vehicle-construction">
                              건설기계 (수기 입력)
                            </label>
                          </div>
                        </div>
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <th>연 식</th>
                  <td>
                    <div className="early-scrap-inline">
                      <Controller
                        name="vehicle.modelYear"
                        control={control}
                        render={({ field }) => (
                          <InputNumber
                            value={field.value}
                            onValueChange={(e) => field.onChange(e.value)}
                            useGrouping={false}
                          />
                        )}
                      />
                      <span>년</span>
                    </div>
                  </td>
                  <th>배기량</th>
                  <td>
                    <div className="early-scrap-inline">
                      <Controller
                        name="vehicle.displacement"
                        control={control}
                        render={({ field }) => (
                          <InputNumber
                            value={field.value}
                            onValueChange={(e) => field.onChange(e.value)}
                            useGrouping={false}
                          />
                        )}
                      />
                      <span>CC</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>
                    차량명
                    <br />
                    (건설기계명)
                  </th>
                  <td>
                    <TextField name="vehicle.vehicleName" control={control} />
                  </td>
                  <th>정원(승합)</th>
                  <td>
                    <div className="early-scrap-inline">
                      <Controller
                        name="vehicle.seatingCapacity"
                        control={control}
                        render={({ field }) => (
                          <InputNumber
                            value={field.value}
                            onValueChange={(e) => field.onChange(e.value)}
                            useGrouping={false}
                          />
                        )}
                      />
                      <span>명</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>
                    규격
                    <br />
                    (건설기계)
                  </th>
                  <td>
                    <TextField
                      name="vehicle.heavyEquipmentSpec"
                      control={control}
                      placeholder="건설기계 선택시 작성"
                      disabled={!isConstructionMachinery}
                    />
                  </td>
                  <th>중량(화물)</th>
                  <td>
                    <div className="early-scrap-inline">
                      <span>적재중량:</span>
                      <TextField
                        name="vehicle.loadWeight"
                        control={control}
                        className="early-scrap-weight-input"
                      />
                      <span>톤 /</span>
                      <span>총중량:</span>
                      <TextField
                        name="vehicle.totalWeight"
                        control={control}
                        className="early-scrap-weight-input"
                      />
                      <span>톤</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="early-scrap-table">
              <thead>
                <tr>
                  <th>확인 항목</th>
                  <th>확인 내용</th>
                  <th>확인 결과</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'identity',
                  'engine',
                  'powerTransmission',
                  'steering',
                  'brake',
                ].map((key) => {
                  const item = EARLY_SCRAP_INSPECTION_BLUEPRINT[key];
                  return (
                    <tr key={key}>
                      <th>{item.title}</th>
                      <td>{item.description}</td>
                      <td>
                        <RadioField
                          name={`inspectionResults.${key}`}
                          control={control}
                          options={item.valueItems}
                        />
                      </td>
                    </tr>
                  );
                })}
                <tr className={isConstructionMachinery ? 'heavy-row' : ''}>
                  <th rowSpan={3}>
                    {EARLY_SCRAP_INSPECTION_BLUEPRINT.hydraulic.title}
                  </th>
                  <td>
                    {EARLY_SCRAP_INSPECTION_BLUEPRINT.hydraulic.description}
                  </td>
                  <td>
                    <RadioField
                      name="inspectionResults.hydraulic"
                      control={control}
                      options={
                        EARLY_SCRAP_INSPECTION_BLUEPRINT.hydraulic.valueItems
                      }
                      disabled={!isConstructionMachinery}
                    />
                  </td>
                </tr>
                <tr className={isConstructionMachinery ? 'heavy-row' : ''}>
                  <td>
                    {
                      EARLY_SCRAP_INSPECTION_BLUEPRINT.workingPartOperation
                        .description
                    }
                  </td>
                  <td>
                    <RadioField
                      name="inspectionResults.workingPartOperation"
                      control={control}
                      options={
                        EARLY_SCRAP_INSPECTION_BLUEPRINT.workingPartOperation
                          .valueItems
                      }
                      disabled={!isConstructionMachinery}
                    />
                  </td>
                </tr>
                <tr className={isConstructionMachinery ? 'heavy-row' : ''}>
                  <td>
                    {
                      EARLY_SCRAP_INSPECTION_BLUEPRINT.workingPartNoise
                        .description
                    }
                  </td>
                  <td>
                    <RadioField
                      name="inspectionResults.workingPartNoise"
                      control={control}
                      options={
                        EARLY_SCRAP_INSPECTION_BLUEPRINT.workingPartNoise
                          .valueItems
                      }
                      disabled={!isConstructionMachinery}
                    />
                  </td>
                </tr>
                <tr>
                  <th rowSpan={5}>그외 주요 부품</th>
                  <td>
                    {EARLY_SCRAP_INSPECTION_BLUEPRINT.exterior.description}
                  </td>
                  <td>
                    <RadioField
                      name="inspectionResults.exterior"
                      control={control}
                      options={
                        EARLY_SCRAP_INSPECTION_BLUEPRINT.exterior.valueItems
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {EARLY_SCRAP_INSPECTION_BLUEPRINT.bumper.description}
                  </td>
                  <td>
                    <RadioField
                      name="inspectionResults.bumper"
                      control={control}
                      options={
                        EARLY_SCRAP_INSPECTION_BLUEPRINT.bumper.valueItems
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {EARLY_SCRAP_INSPECTION_BLUEPRINT.glass.description}
                  </td>
                  <td>
                    <RadioField
                      name="inspectionResults.glass"
                      control={control}
                      options={
                        EARLY_SCRAP_INSPECTION_BLUEPRINT.glass.valueItems
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {EARLY_SCRAP_INSPECTION_BLUEPRINT.tire.description}
                  </td>
                  <td>
                    <RadioField
                      name="inspectionResults.tire"
                      control={control}
                      options={
                        EARLY_SCRAP_INSPECTION_BLUEPRINT.tire.valueItems
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    {EARLY_SCRAP_INSPECTION_BLUEPRINT.lighting.description}
                  </td>
                  <td>
                    <RadioField
                      name="inspectionResults.lighting"
                      control={control}
                      options={
                        EARLY_SCRAP_INSPECTION_BLUEPRINT.lighting.valueItems
                      }
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="early-scrap-table">
              <tbody>
                <tr>
                  <th rowSpan={2} className="category-th">
                    첨<br />부<br />물
                  </th>
                  <th>확인 방식</th>
                  <td colSpan={3}>
                    <Controller
                      name="confirmationMethod"
                      control={control}
                      render={({ field }) => (
                        <div className="early-scrap-confirm-method">
                          {EARLY_SCRAP_ATTACHMENT_BLUEPRINT.confirmationMethod.valueItems.map(
                            (option) => (
                              <div
                                key={option.value}
                                className="early-scrap-confirm-option"
                              >
                                <div className="early-scrap-radio-item">
                                  <RadioButton
                                    inputId={`confirmation-${option.value}`}
                                    name={field.name}
                                    value={option.value}
                                    onChange={(e) => field.onChange(e.value)}
                                    checked={field.value === option.value}
                                  />
                                  <label
                                    htmlFor={`confirmation-${option.value}`}
                                  >
                                    {option.label}
                                  </label>
                                </div>
                                <p className="early-scrap-confirm-desc">
                                  {option.description}
                                </p>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    />
                  </td>
                </tr>
                <tr>
                  <th>
                    {isOnlineConfirmation
                      ? '동영상 안내'
                      : '대상차량 확인 사진'}
                  </th>
                  <td colSpan={3}>
                    {isOnlineConfirmation ? (
                      <p className="early-scrap-online-guide">
                        온라인 대상차량확인 시스템(
                        <a
                          href="https://escar.or.kr"
                          target="_blank"
                          rel="noreferrer"
                        >
                          escar.or.kr
                        </a>
                        )에 등록된 동영상으로 갈음합니다. 현장 사진 첨부는 생략할
                        수 있습니다.
                      </p>
                    ) : (
                      <div className="early-scrap-photo-grid">
                        {EARLY_SCRAP_PHOTO_BLUEPRINT.positions.map(
                          ({ position, htmlLabel }) => (
                            <label
                              key={position}
                              className="early-scrap-photo-box"
                              htmlFor={`photo-${position}`}
                            >
                              {!photoPreviews[position] && (
                                <>
                                  <span className="early-scrap-photo-icon">
                                    📷
                                  </span>
                                  <span className="early-scrap-photo-label">
                                    {htmlLabel}
                                  </span>
                                </>
                              )}
                              {photoPreviews[position] && (
                                <img
                                  src={photoPreviews[position]}
                                  alt={htmlLabel}
                                  className="early-scrap-photo-preview"
                                />
                              )}
                              <input
                                id={`photo-${position}`}
                                type="file"
                                accept="image/*"
                                onChange={(event) =>
                                  handlePhotoChange(position, event)
                                }
                              />
                            </label>
                          )
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="early-scrap-table">
              <tbody>
                <tr>
                  <th>{EARLY_SCRAP_JUDGMENT_BLUEPRINT.operationalStatus.title}</th>
                  <td>
                    <RadioField
                      name="judgment.operationalStatus"
                      control={control}
                      options={
                        EARLY_SCRAP_JUDGMENT_BLUEPRINT.operationalStatus
                          .valueItems
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <th>{EARLY_SCRAP_JUDGMENT_BLUEPRINT.failReason.title}</th>
                  <td>
                    <TextField
                      name="judgment.failReason"
                      control={control}
                      placeholder={
                        EARLY_SCRAP_JUDGMENT_BLUEPRINT.failReason.placeholder
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <th>{EARLY_SCRAP_JUDGMENT_BLUEPRINT.overallOpinion.title}</th>
                  <td>
                    <Controller
                      name="judgment.overallOpinion"
                      control={control}
                      render={({ field }) => (
                        <InputTextarea
                          {...field}
                          rows={2}
                          className="w-full"
                          placeholder={
                            EARLY_SCRAP_JUDGMENT_BLUEPRINT.overallOpinion
                              .placeholder
                          }
                        />
                      )}
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="early-scrap-actions">
              <Button
                type="button"
                label="인쇄 / 출력하기"
                icon="pi pi-print"
                className="p-button-success"
                onClick={() => {
                  applyEarlyScrapPrintPageStyle();
                  window.print();
                }}
              />
              <Button
                type="submit"
                label="점검서 저장"
                icon="pi pi-save"
                loading={loading}
              />
              <Button
                type="button"
                label="닫기"
                icon="pi pi-times"
                className="p-button-secondary"
                onClick={() => window.close()}
              />
            </div>
          </form>
        </div>

        <EarlyScrapPrintDocument
          formValues={formValues}
          photoPreviews={photoPreviews}
        />
      </div>
    </BlockUI>
  );
};

export default EarlyScrapRegistrationForm;
